import { useEffect, useRef, useState } from "react";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { downloadSalesImportFile, getSalesImport } from "../api/salesImportsApi";
import SalesImportCardShell from "../components/SalesImportCardShell";
import SalesImportDraftSummary from "../components/SalesImportDraftSummary";
import SalesImportStepFour from "../components/SalesImportStepFour";
import { normalizeSalesImportDetail } from "../utils/salesImportDetailModel";
import { getSalesImportErrorMessage } from "../utils/salesImportError";
import { formatSalesImportDateTime } from "../utils/salesImportListModel";

const LIST_PATH = "/stock/operacion/ventas-importadas";

export default function SalesImportDetailPage() {
  const { importId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const downloadController = useRef(null);
  const downloadInFlight = useRef(false);

  useEffect(() => {
    setDownloadLoading(false);
    setDownloadError("");
    return () => {
      downloadController.current?.abort();
      downloadController.current = null;
      downloadInFlight.current = false;
    };
  }, [importId]);

  useEffect(() => {
    const controller = new AbortController();
    const id = Number(importId);
    if (!Number.isSafeInteger(id) || id <= 0) {
      setDetail(null);
      setError("La importación solicitada no es válida.");
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError("");
    getSalesImport(id, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) setDetail(normalizeSalesImportDetail(response));
      })
      .catch((requestError) => {
        if (!controller.signal.aborted && requestError?.name !== "AbortError") {
          setDetail(null);
          setError(getSalesImportErrorMessage(requestError, "No se pudo cargar el detalle de la importación."));
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [importId, retry]);

  function goBack() {
    navigate(LIST_PATH, {
      state: location.state?.salesImportListState
        ? { salesImportListState: location.state.salesImportListState }
        : undefined,
    });
  }

  async function downloadFile() {
    if (downloadInFlight.current || !detail?.fileAvailable) return;
    const controller = new AbortController();
    downloadInFlight.current = true;
    downloadController.current = controller;
    setDownloadLoading(true);
    setDownloadError("");
    try {
      await downloadSalesImportFile(detail.id, { signal: controller.signal });
    } catch (requestError) {
      if (!controller.signal.aborted && requestError?.name !== "AbortError") {
        setDownloadError(getSalesImportErrorMessage(requestError, "No se pudo descargar el archivo original."));
      }
    } finally {
      if (downloadController.current === controller) {
        downloadController.current = null;
        downloadInFlight.current = false;
        if (!controller.signal.aborted) setDownloadLoading(false);
      }
    }
  }

  return (
    <Stack spacing={2} sx={{ p: 2, flex: 1, minHeight: 0, overflow: "hidden" }}>
      <StockPageHeader title="Detalle de ventas importadas" onBack={goBack} />
      <SalesImportCardShell sx={{ overflowX: "hidden", overflowY: "auto" }}>
        {loading ? <Stack direction="row" spacing={1} alignItems="center" role="status"><CircularProgress size={20} /><Typography>Cargando detalle de ventas importadas…</Typography></Stack> : null}
        {!loading && error ? <Stack spacing={1} alignItems="flex-start"><Alert severity="error">{error}</Alert><Button onClick={() => setRetry((value) => value + 1)}>Reintentar</Button></Stack> : null}
        {!loading && detail ? (
          <Stack spacing={2} sx={{ minHeight: 0 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} spacing={1.5}>
              <Typography component="h2" sx={{ color: "primary.900", fontSize: 18, fontWeight: 800 }}>Resumen de la importación</Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", minWidth: 0 }}>
                {detail.fileAvailable ? (
                  <Button
                    type="button"
                    size="small"
                    disabled={downloadLoading}
                    onClick={downloadFile}
                    startIcon={<DownloadOutlinedIcon sx={{ fontSize: 17 }} />}
                    sx={{ flexShrink: 0, px: 1.5, py: 0.6, borderRadius: "20px", bgcolor: "primary.500", color: "logoblanco", textTransform: "none", fontSize: 12, fontWeight: 750, "&:hover": { bgcolor: "primary.600" } }}
                  >
                    {downloadLoading ? "Descargando…" : "Descargar archivo importado"}
                  </Button>
                ) : <Typography sx={{ color: "neutral.700", fontSize: 12 }}>Archivo original no disponible para descarga.</Typography>}
              </Box>
            </Stack>
            {downloadError ? <Alert severity="error">{downloadError}</Alert> : null}
            {detail.historicalDataAvailable ? (
              <>
                <SalesImportStepFour draft={detail.draft} preview={detail.preview} treasury={detail.treasury} historical showTitle={false} />
                <Typography sx={{ color: "neutral.700", fontSize: 12, overflowWrap: "anywhere" }}>
                  Archivo: {detail.originalFileName || "—"} · Importado por: {detail.integratedBy?.name || "—"} · Fecha de importación: {formatSalesImportDateTime(detail.integratedAt)}. Los valores de Stock corresponden al momento de la integración.
                </Typography>
              </>
            ) : (
              <>
                <Alert severity="info">Esta importación no tiene un desglose histórico de Stock y Tesorería. Se muestran únicamente los datos de ventas disponibles.</Alert>
                <SalesImportDraftSummary draft={{ ...detail.draft, id: detail.id }} />
              </>
            )}
          </Stack>
        ) : null}
      </SalesImportCardShell>
    </Stack>
  );
}
